import { Metrics, MetricValueContext, METRIC_KEY } from "calculation/metric";
import {
  Parameters,
  ParameterValueContext,
  ParameterValues,
  PARAMETER_KEY,
} from "calculation/parameter";
import { DistrictDto, ScenarioDto } from "dtos/dtos";
import { saveAs } from "file-saver";
import moment from "moment";
import { CalculationState } from "state/state";

export async function exportZonTypGdeCsv(
  metrics: Metrics,
  calculationState: CalculationState,
  scenario: ScenarioDto,
  gemeinde: string
) {
  const metadataHeader =
    "bfs_nr_1999;Zonentyp Gemeinde;zontyp_pbg;zontyp_kt;zontyp_gde";
  const header = [
    metadataHeader,
    ...Object.values(METRIC_KEY).map(
      (key) => `${metrics.getMetric(key)?.name} (${key})`
    ),
  ].join(";");
  const rows: string[] = [];

  for (const district of scenario.districts) {
    for (const zoneGde of district.zonesGde.sort(
      (a, b) => a.zonTypGde - b.zonTypGde
    )) {
      const metadata = `${district.bfsNr1999};${zoneGde.nameZonTypGde};${zoneGde.zonTypPbg};${zoneGde.zonTypKt};${zoneGde.zonTypGde}`;
      const values: string[] = [];
      for (const metricKey of Object.values(METRIC_KEY)) {
        const metricValue = calculationState.metricValues.getValue(
          new MetricValueContext(metricKey, {
            name: "districtZoneGde",
            bfsNr: scenario.bfsNr,
            bfsNr1999: district.bfsNr1999,
            zonTypGde: zoneGde.zonTypGde,
            zonTypKt: zoneGde.zonTypKt,
            zonTypPbg: zoneGde.zonTypPbg,
          })
        );

        const value =
          metricValue !== undefined ? metricValue.value.toString() : "-";
        values.push(value);
      }

      rows.push([metadata, values.join(";")].join(";"));
    }
  }

  const data = [header, rows.join("\n")].join("\n");
  // Excel needs BOM
  const blob = new Blob(["\uFEFF" + data], {
    type: "text/csv;charset=utf-8",
  });
  const timestamp = moment().format("YYYYMMDD-HHmmss");
  saveAs(
    blob,
    `Bauzonenkapazität-pro-Zonentyp-Gemeinde-${gemeinde}-${timestamp}.csv`
  );
}

export async function exportZonTypPbgCsv(
  metrics: Metrics,
  calculationState: CalculationState,
  scenario: ScenarioDto,
  gemeinde: string
) {
  const metadataHeader = "Zonentyp PBG;zontyp_pbg";
  const header = [
    metadataHeader,
    ...Object.values(METRIC_KEY).map(
      (key) => `${metrics.getMetric(key)?.name} (${key})`
    ),
  ].join(";");
  const rows: string[] = [];

  const zonTypPbgSet = new Set<number>();
  const namesZonTypPbg: Record<number, string> = {};

  for (const district of scenario.districts) {
    for (const zoneGde of district.zonesGde) {
      zonTypPbgSet.add(zoneGde.zonTypPbg);
      namesZonTypPbg[zoneGde.zonTypPbg] = zoneGde.nameZonTypPbg;
    }
  }

  const zonTypPbgs = Array.from(zonTypPbgSet).sort();

  for (const zonTypPbg of zonTypPbgs) {
    const metadata = `${namesZonTypPbg[zonTypPbg]};${zonTypPbg}`;
    const values: string[] = [];
    for (const metricKey of Object.values(METRIC_KEY)) {
      const metricValue = calculationState.metricValues.getValue(
        new MetricValueContext(metricKey, {
          name: "zonTypPbg",
          bfsNr: scenario.bfsNr,
          zonTypPbg: zonTypPbg,
        })
      );

      const value =
        metricValue !== undefined ? metricValue.value.toString() : "-";
      values.push(value);
    }

    rows.push([metadata, values.join(";")].join(";"));
  }

  const data = [header, rows.join("\n")].join("\n");
  // Excel needs BOM
  const blob = new Blob(["\uFEFF" + data], {
    type: "text/csv;charset=utf-8",
  });
  const timestamp = moment().format("YYYYMMDD-HHmmss");
  saveAs(
    blob,
    `Bauzonenkapazität-pro-Zonentyp-PBG-${gemeinde}-${timestamp}.csv`
  );
}

export async function pdfReport(
  calculationState: CalculationState,
  gemeinde: string,
  gdeKatCode: string,
  scenario: ScenarioDto,
  parameterSetModifiedAt: string
): Promise<boolean> {
  const parameterSetModifiedAtFormatted = moment(parameterSetModifiedAt).format(
    "DD.MM.YYYY"
  );
  const scenarioName = `${gemeinde}_${scenario.status}_${moment(
    scenario.createdAt
  ).format("DDMMYYYY")}`;
  const zonTypPbgSet = new Set<number>();
  const namesZonTypPbg: Record<number, string> = {};

  for (const district of scenario.districts) {
    for (const zoneGde of district.zonesGde) {
      zonTypPbgSet.add(zoneGde.zonTypPbg);
      namesZonTypPbg[zoneGde.zonTypPbg] = zoneGde.nameZonTypPbg;
    }
  }

  const zonTypPbgs = Array.from(zonTypPbgSet).sort();

  let highestDichteKat = 1000000;
  const dichteKatToDistrict: Record<number, DistrictDto[]> = {};

  for (const district of scenario.districts) {
    if (district.dichteKat < highestDichteKat) {
      highestDichteKat = district.dichteKat;
    }

    if (!(district.dichteKat in dichteKatToDistrict)) {
      dichteKatToDistrict[district.dichteKat] = [];
    }

    dichteKatToDistrict[district.dichteKat].push(district);
  }

  let finalDistrict = dichteKatToDistrict[highestDichteKat].reduce(
    (result, district) => {
      const mBuildingAreaDemandExpected =
        calculationState.metricValues.getValue(
          new MetricValueContext(METRIC_KEY.mBuildingAreaDemandExpected, {
            name: "district",
            bfsNr1999: district.bfsNr1999,
            bfsNr: scenario.bfsNr,
          })
        )!.value;

      if (mBuildingAreaDemandExpected < result.mBuildingAreaDemandExpected) {
        return {
          district: district,
          mBuildingAreaDemandExpected: mBuildingAreaDemandExpected,
        };
      }

      return result;
    },
    {
      district: dichteKatToDistrict[highestDichteKat][0],
      mBuildingAreaDemandExpected: 1000000,
    }
  ).district;

  const compensation = calculationState.metricValues.getValue(
    new MetricValueContext(METRIC_KEY.compensation, {
      name: "district",
      bfsNr1999: finalDistrict.bfsNr1999,
      bfsNr: scenario.bfsNr,
    })
  )!.value;

  const data = {
    gemeinde: gemeinde,
    scenario_date: parameterSetModifiedAtFormatted,
    scenario_name: scenarioName,
    scenario_legal: scenario.status,
    compensation: compensation === 1 ? true : false,
    bfs_nr: scenario.bfsNr,
    gdekat_code: gdeKatCode,
    population_hist: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationHist, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    Q_population_future: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.qPopulationFuture, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    A_population_actual: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.aPopulationActual, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    B_population_capacity_total: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.bPopulationCapacityTotal, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    C_population_growth: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.cPopulationGrowth, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    D_population_growth_percent: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.dPopulationGrowthPercent, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    building_area_demand: scenario.districts.map((d) => {
      return {
        district_name: d.gem1999,
        M_building_area_demand_expected: calculationState.metricValues.getValue(
          new MetricValueContext(METRIC_KEY.mBuildingAreaDemandExpected, {
            name: "district",
            bfsNr1999: d.bfsNr1999,
            bfsNr: scenario.bfsNr,
          })
        )?.value,
        L_building_area_demand_acutal: calculationState.metricValues.getValue(
          new MetricValueContext(METRIC_KEY.lBuildingAreaDemandActual, {
            name: "district",
            bfsNr1999: d.bfsNr1999,
            bfsNr: scenario.bfsNr,
          })
        )?.value,
        O_building_area_demand_comparison:
          calculationState.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.oBuildingAreaDemandComparison, {
              name: "district",
              bfsNr1999: d.bfsNr1999,
              bfsNr: scenario.bfsNr,
            })
          )?.value,
      };
    }),
    P_var_factor_rp: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.pVarFactorRp, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    R_population_new_zones_demand: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.rPopulationNewZonesDemand, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    E_population_growth_annual: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.ePopulationGrowthAnnual, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    F_population_growth_annual_percent: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.fPopulationGrowthAnnualPercent, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_pot_covered: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationPotCovered, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_relevant: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationRelevant, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    summary_table_bz: zonTypPbgs
      .sort()
      .filter((zonTypPbg) => zonTypPbg < 6000)
      .map((zonTypPbg) => {
        return {
          zone_pbg_name: namesZonTypPbg[zonTypPbg],
          zone_pbg: zonTypPbg,
          fl_bebaut: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fBebaut, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity_covered: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.populationCapacityCovered, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          fl_unbebaut: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fUnbebaut, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity_uncovered:
            calculationState!.metricValues.getValue(
              new MetricValueContext(METRIC_KEY.populationCapacityUncovered, {
                name: "zonTypPbg",
                zonTypPbg: zonTypPbg,
                bfsNr: scenario!.bfsNr,
              })
            )?.value,
          fl: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fTotal, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.populationCapacity, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
        };
      }),
    summary_table_nbz: zonTypPbgs
      .filter((zonTypPbg) => zonTypPbg >= 6000)
      .map((zonTypPbg) => {
        return {
          zone_pbg_name: namesZonTypPbg[zonTypPbg],
          zone_pbg: zonTypPbg,
          fl_bebaut: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fBebaut, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity_covered: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.populationCapacityCovered, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          fl_unbebaut: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fUnbebaut, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity_uncovered:
            calculationState!.metricValues.getValue(
              new MetricValueContext(METRIC_KEY.populationCapacityUncovered, {
                name: "zonTypPbg",
                zonTypPbg: zonTypPbg,
                bfsNr: scenario!.bfsNr,
              })
            )?.value,
          fl: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.fTotal, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
          population_capacity: calculationState!.metricValues.getValue(
            new MetricValueContext(METRIC_KEY.populationCapacity, {
              name: "zonTypPbg",
              zonTypPbg: zonTypPbg,
              bfsNr: scenario!.bfsNr,
            })
          )?.value,
        };
      }),
    population_capacity_uncovered: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationCapacityUncovered, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_capacity_covered: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationCapacityCovered, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_capacity: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationCapacity, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_variation: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationVariation, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_correction: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationCorrection, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_temporary: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationTemporary, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_pending: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationPending, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    population_kgwr_final: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.populationKgwrFinal, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    param_year_previous_rp: calculationState.parameterValues.getValue(
      new ParameterValueContext(PARAMETER_KEY.paramYearPreviousRp, {
        name: "canton",
      })
    )?.value,
    param_relevant_year_population: calculationState.parameterValues.getValue(
      new ParameterValueContext(PARAMETER_KEY.paramRelevantYearPopulation, {
        name: "canton",
      })
    )?.value,
    param_observ_period: calculationState.parameterValues.getValue(
      new ParameterValueContext(PARAMETER_KEY.paramObservPeriod, {
        name: "canton",
      })
    )?.value,
    fl_bebaut: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.fBebaut, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    fl_unbebaut: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.fUnbebaut, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
    fl: calculationState.metricValues.getValue(
      new MetricValueContext(METRIC_KEY.fTotal, {
        name: "municipality",
        bfsNr: scenario.bfsNr,
      })
    )?.value,
  };

  const formData = new URLSearchParams();
  formData.append(
    "report_as_JSON",
    JSON.stringify({
      template: {
        name: "/LUBAT/lubat_report",
      },
      data: data,
    })
  );
  formData.append("f", "json");

  try {
    let response = await fetch(`${process.env.REACT_APP_PDF_SERVICE_URL}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: formData.toString(),
    });

    if (response.ok) {
      const result = await response.json();
      var a = document.createElement("a");
      a.href = result.results[0].value.url;
      a.target = "_blank";
      a.download = "report.pdf";
      a.click();
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

export async function exportParametersCsv(
  parameters: Parameters,
  parameterValues: ParameterValues,
  scenario: ScenarioDto,
  parameterSetName: string,
  gemeinde: string
) {
  const rows: string[] = [];

  for (const p of parameters
    .getAllParameters()
    .sort((a, b) => (a.key < b.key ? -1 : 1))) {
    if (p.dimensionsValue === 0) {
      const value = parameterValues.getValue(
        new ParameterValueContext(p.key, {
          name: "canton",
        })
      );

      if (value !== undefined) {
        rows.push(
          `${p.key};${p.name};-;-;-;-;${value.value};${value.displayValue}`
        );
      }
    } else if (p.dimensionsValue === 1) {
      for (const district of scenario.districts.sort((a, b) =>
        a.gem1999 < b.gem1999 ? -1 : 1
      )) {
        const value = parameterValues.getValue(
          new ParameterValueContext(p.key, {
            name: "district",
            bfsNr1999: district.bfsNr1999,
          })
        );

        if (value !== undefined) {
          rows.push(
            `${p.key};${p.name};${district.gem1999};${district.bfsNr1999};-;-;${value.value};${value.displayValue}`
          );
        }
      }
    } else if (p.dimensionsValue === 2) {
      for (const district of scenario.districts.sort((a, b) =>
        a.gem1999 < b.gem1999 ? -1 : 1
      )) {
        for (const zoneGde of district.zonesGde.sort(
          (a, b) => a.zonTypGde - b.zonTypGde
        )) {
          const value = parameterValues.getValue(
            new ParameterValueContext(p.key, {
              name: "districtZoneGde",
              bfsNr1999: district.bfsNr1999,
              zonTypGde: zoneGde.zonTypGde,
            })
          );

          if (value !== undefined) {
            rows.push(
              `${p.key};${p.name};${district.gem1999};${district.bfsNr1999};${zoneGde.nameZonTypGde};${zoneGde.zonTypGde};${value.value};${value.displayValue}`
            );
          } else {
            console.log("Value undefined");
          }
        }
      }
    }
  }

  const header =
    "Parameterschlüssel;Parametername;Ortsteil;bfs_nr_1999;Zonentyp Gemeinde;zontyp_gde;Wert (Zahl);Wert (Text)";
  const data = [header, ...rows].join("\n");
  // Excel needs BOM
  const blob = new Blob(["\uFEFF" + data], {
    type: "text/csv;charset=utf-8",
  });
  const timestamp = moment().format("YYYYMMDD-HHmmss");
  saveAs(blob, `Kennwerte-${gemeinde}-${parameterSetName}-${timestamp}.csv`);
}
